import React, { FC, useCallback, useEffect, useState } from 'react';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  createStyles,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Slide,
  Typography,
  WithStyles,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import MuiDialogContent from '@material-ui/core/DialogContent';
import MuiDialogActions from '@material-ui/core/DialogActions';
import Dialog from '@material-ui/core/Dialog';
import { Theme, withStyles } from '@material-ui/core/styles';

import { ImportPayload } from '../../../api/types';
import TextField from '../text-field';

export type NewEquipType = {
  type?: EntityBase;
  model?: EntityBase;
  brand?: EntityBase;
  installDate?: string;
  photos?: Array<ImportPayload>;
};

interface StagedSelectProps extends WithStyles<typeof styles> {
  open?: boolean;
  title: string;
  guideName: string;
  onClose(open: false): void;
  onApprove(selectedItem: EntityBase, itemsChain: EntityBase[]): void;
  onSelect(stage: number, selectedItem: EntityBase): boolean;
  onFetchOptions(
    stage: number,
    selectedItem?: EntityBase
  ): Promise<EntityBase[]>;
  onGetGuideName(stage: number, item: EntityBase): string;
}

const StagedSelect: FC<StagedSelectProps> = (props) => {
  const {
    title,
    open,
    onClose,
    onApprove,
    onSelect,
    onGetGuideName,
    classes,
    onFetchOptions,
    guideName: _guideName,
  } = props;

  const [items, setItems] = useState<EntityBase[]>([]);

  const [itemsChain, setItemsChain] = useState<EntityBase[]>([]);

  const [loading, setLoading] = useState<boolean>(true);

  const [stage, setStage] = useState<number>(0);
  const [guideName, setGuideName] = useState<string>(_guideName);

  useEffect(() => {
    if (!open) {
      return;
    }
    setLoading(true);
    onFetchOptions(0).then((items) => {
      setItems(items);
      setLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const _goBack = useCallback(() => {
    if (stage === 0) {
      return;
    }
    setStage(stage - 1);
    const newChain = itemsChain.slice(0, -1);
    setItemsChain(newChain);
    setGuideName(
      stage > 1 ? onGetGuideName(stage - 1, newChain.pop()) : _guideName
    );
    setLoading(true);
    onFetchOptions(0).then((items) => {
      setItems(items);
      setLoading(false);
    });
  }, [stage, itemsChain, onGetGuideName, _guideName, onFetchOptions]);

  const _onClose = useCallback(() => {
    onClose(false);
    setStage(0);
    setItems([]);
    setItemsChain([]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onClick = useCallback(
    (item: EntityBase) => () => {
      if (onSelect(stage, item)) {
        setItemsChain([...itemsChain, item]);
        setStage(stage + 1);
        setGuideName(onGetGuideName(stage + 1, item));
        setLoading(true);
        onFetchOptions(stage + 1, item).then((items) => {
          setItems(items);
          setLoading(false);
        });
      } else {
        onApprove(item, [...itemsChain, item]);
        _onClose();
      }
    },
    [onSelect, stage, itemsChain, onGetGuideName, onFetchOptions, onApprove, _onClose]
  );

  const getList = (guide: EntityBase[], loading: boolean) => (
    <Box>
      <Box marginX={2}>
        <Typography variant="h6">{guideName}</Typography>
      </Box>
      <List className={classes.list}>
        {!loading &&
          guide.map((item) => (
            <ListItem
              key={`key-${stage}-${item.id}`}
              className={classes.listItem}
              onClick={onClick(item)}
            >
              <ListItemAvatar>
                <Avatar color="secondary">
                  {item.name
                    .split(' ')
                    .slice(0, 2)
                    .map((word) => word[0].toUpperCase())
                    .join('')}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Typography color="textSecondary">{item.name}</Typography>
                }
              />
            </ListItem>
          ))}
        {loading && (
          <ListItem className={classes.listItemInfo}>
            <CircularProgress size={24} />
          </ListItem>
        )}
        {!loading && guide.length === 0 && (
          <ListItem className={classes.listItemInfo}>Пусто</ListItem>
        )}
      </List>
    </Box>
  );

  return (
    <>
      <Dialog open={open || false} onClose={_onClose} maxWidth="lg">
        <MuiDialogTitle disableTypography className={classes.root}>
          <Typography variant="h6">{title}</Typography>
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            onClick={() => onClose(false)}
          >
            <CloseIcon />
          </IconButton>
        </MuiDialogTitle>
        <MuiDialogContent dividers className={classes.content}>
          <Box px={2}>
            <TextField label="Поиск" id="search" />
          </Box>
          <Slide direction="left" in={!loading}>
            {getList(items, loading)}
          </Slide>
        </MuiDialogContent>
        <MuiDialogActions>
          {stage !== 0 && <Button onClick={_goBack}>Назад</Button>}
          <Button onClick={_onClose}>Отмена</Button>
        </MuiDialogActions>
      </Dialog>
    </>
  );
};

const styles = (theme: Theme) =>
  createStyles({
    root: {
      margin: 0,
      padding: theme.spacing(2),
    },
    content: {
      overflowX: 'hidden',
      height: 'calc(100vh - 300px)',
      padding: '16px 0px',
      minWidth: 'calc(100vw - 64px)',
      display: 'grid',
      gridTemplateColumns: '100% 100% 100%',
    },
    closeButton: {
      position: 'absolute',
      right: theme.spacing(1),
      top: theme.spacing(1),
      color: theme.palette.grey[500],
    },
    name: {
      color: theme.palette.primary.main,
      marginRight: theme.spacing(4),
      marginLeft: theme.spacing(2),
    },
    value: {
      color: theme.palette.primary.main,
    },
    list: {
      padding: '0 !important',
      overflowY: 'auto',
      width: '100%',
    },
    listItem: {
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
      },
    },
    listItemInfo: {
      justifyContent: 'center',
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
      },
    },
  });

export default withStyles(styles)(StagedSelect);
